import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { FamilyMember } from "../../types/family";

export type MembershipAction = "leave" | "remove";

export function MembershipActionConfirm({
  action,
  member,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  action: MembershipAction;
  member: FamilyMember;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const leaving = action === "leave";

  useEffect(() => {
    const previousFocus = document.activeElement;
    confirmRef.current?.focus();
    return () => { if (previousFocus instanceof HTMLElement) previousFocus.focus(); };
  }, []);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose, pending]);

  const trapFocus = (event: ReactKeyboardEvent) => {
    if (event.key !== "Tab") return;
    if (event.shiftKey && document.activeElement === confirmRef.current) {
      event.preventDefault();
      cancelRef.current?.focus();
    } else if (!event.shiftKey && document.activeElement === cancelRef.current) {
      event.preventDefault();
      confirmRef.current?.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <section role="dialog" aria-modal="true" aria-labelledby="membership-action-title" aria-describedby="membership-action-description" onKeyDown={trapFocus} className="w-full max-w-sm rounded-[28px] bg-white p-6 text-center shadow-2xl">
        <h2 id="membership-action-title" className="text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">
          {leaving ? "Покинуть семью?" : `Удалить ${member.name}?`}
        </h2>
        <p id="membership-action-description" className="mt-3 text-sm leading-6 text-[#77776F]">
          {leaving ? "Ваши активные бронирования будут освобождены." : "Активные бронирования участника будут освобождены."}
        </p>
        {error && <div role="alert" className="mt-4 rounded-2xl bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#B91C1C]">{error}</div>}
        <div className="mt-6 flex flex-col gap-3">
          <button ref={confirmRef} type="button" onClick={onConfirm} disabled={pending} className="h-12 rounded-xl bg-[#C83D32] text-sm font-bold text-white disabled:cursor-wait disabled:bg-[#FCA5A5]">
            {pending ? "Подождите…" : leaving ? "Покинуть семью" : "Удалить участника"}
          </button>
          <button ref={cancelRef} type="button" onClick={onClose} disabled={pending} className="h-12 rounded-xl bg-[#F2F1EC] text-sm font-bold text-[#171714] disabled:cursor-not-allowed disabled:opacity-60">Отмена</button>
        </div>
      </section>
    </div>
  );
}
